import {
    RiAccountBoxFill, RiArrowDownSLine, RiArrowUpSLine, RiBuilding4Line, RiExternalLinkFill,
    RiFacebookBoxFill,
    RiLinkedinBoxFill,
    RiUser3Fill,
} from "@remixicon/react";
import classNames from "classnames";
import React, { ReactElement, useState } from "react";
import {
    IntegrationDataGroupType, IntegrationDataValueGroup, IntegrationType, IntegrationValueLinkIcon, useGetIntegrationUserQuery,
} from "../../__generated__/api";
import { ReactComponent as TwitterXFill } from "../../assets/icons/twitter-x-fill.svg";
import { LoggingHandler } from "../../contexts/logging/LoggingContext";
import { useTheme } from "../../contexts/Theme";
import Avatar from "../avatar/Avatar";
import DisplayValue from "../display-value/DisplayValue";
import ExpandingSection from "../expanding-section/ExpandingSection";
import Loading from "../loading/Loading";
import Button from "../theme/button/Button";
import Styles from "./IntegrationUserData.module.scss";

interface Props {
    userId: string;
    metricPathPrefix?: string[];
}

interface GroupProps {
    group: IntegrationDataValueGroup;
    shouldDisplayGroupName: boolean
}

const ICON_NAME_TO_ICON = {
    [IntegrationValueLinkIcon.Profile]: <RiAccountBoxFill />,
    [IntegrationValueLinkIcon.External]: <RiExternalLinkFill />,
    [IntegrationValueLinkIcon.Linkedin]: <RiLinkedinBoxFill />,
    [IntegrationValueLinkIcon.Twitter]: <TwitterXFill />,
    [IntegrationValueLinkIcon.Facebook]: <RiFacebookBoxFill />,
};

const TYPE_TO_ICON = {
    [IntegrationDataGroupType.Contact]: <RiUser3Fill />,
    [IntegrationDataGroupType.Organization]: <RiBuilding4Line />,
};

function IntegrationUserDataSection({ group, shouldDisplayGroupName }: GroupProps): ReactElement {
    const [isExpanded, setExpanded] = useState(false);
    const theme = useTheme();

    let filtered = group.values;
    if (!isExpanded) {
        filtered = filtered.filter(value => value.value && value.isDefaultDisplayed);
    }
    const icon = group.type ? TYPE_TO_ICON[group.type] : undefined;
    return (
        <div className={classNames(
            Styles["card-group-section-container"],
            shouldDisplayGroupName ? Styles["card-group-section-container-named"] : "",
        )}
        >
            {shouldDisplayGroupName && (
                <div className={classNames(" mt-3  w-100 d-flex justify-content-between", theme.isV3
                    ? Styles["group-header"]
                    : "text-blue-dark fw-bolder mb-2 vertical")}
                >
                    <span className={Styles["group-name"]}>
                        {icon}
                        {group.label}
                    </span>
                    {group.links?.isNotEmpty && (
                        <div className="">
                            {group.links?.map(link => (
                                <a
                                    href={link.url as string}
                                    key={link.url as string}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="btn btn-none ms-2 p-0 align-top text-light"
                                >
                                    <span aria-hidden="true" className="visually-hidden">{link.label}</span>
                                    {ICON_NAME_TO_ICON[link.icon]}
                                </a>
                            ))}
                        </div>
                    )}

                </div>
            )}

            <div className="list-group list-group-flush">
                {filtered.map(value => (
                    <div
                        className={theme.isV3 ? Styles["item-row"] : "mb-2 list-group-item border-0 p-0 text-gray-800"}
                        key={value.label}
                    >
                        <small className={theme.isV3 ? Styles["item-title"] : "text-gray-500 col-4"}>
                            {value.label}
                        </small>
                        <div className={theme.isV3 ? Styles["item-value"] : "col"}>
                            <DisplayValue type={value.type} value={value.value as string} text={value.text as string} />
                        </div>
                    </div>
                ))}
            </div>
            {(filtered.length < group.values.length || isExpanded) && (
                <Button
                    variant="none"
                    className={!theme.isV3 ? "btn btn-link btn-sm d-block p-0" : Styles["show-more"]}
                    onClick={() => setExpanded(value => !value)}
                >
                    {isExpanded ? (
                        <>
                            <RiArrowUpSLine />
                            Show Less
                        </>
                    ) : (
                        <>
                            <RiArrowDownSLine />
                            Show More
                        </>
                    )}

                </Button>
            )}
        </div>
    );
}

export default function IntegrationUserData({ userId, metricPathPrefix }: Props): ReactElement {
    const { data, loading, error } = useGetIntegrationUserQuery({
        variables: {
            id: userId,
        },
    });
    const theme = useTheme();
    if (loading) {
        return theme.isV3 ? (
            <div className={classNames(Styles.loading, "loading")}>
                <div>
                    Section
                </div>
                <div>
                    Section
                </div>
            </div>

        ) : <Loading />;
    }
    if (error || !data) {
        return (
            <div className="p-2">
                Error loading user profile...
            </div>
        );
    }

    const classNamesList: string[] = [];
    if (!theme.isV3) {
        classNamesList.push("list-group-item text-gray-800 mb-3 px-3 mt-3 ");
        classNamesList.push(Styles[`user-group`]);
    } else {
        classNamesList.push(Styles[`user-group-v3`]);
    }

    return (
        <div>

            <div className="h-100 d-flex flex-column">

                <div className={classNames("list-group list-group-flush flex-grow-1", theme.isV3 ? "" : "ms-2")}>
                    {data.integrationUserData?.filter(integration => integration.groups.isNotEmpty).map(integration => {
                        const titleBlock = (
                            <div className={Styles["title-block"]}>
                                {theme.isV3 && integration.iconUrl && (
                                    <Avatar
                                        src={integration.iconUrl}
                                        variant={theme.isV3 ? "sm" : "xs"}
                                        isCircle
                                        className={Styles["company-avatar"]}
                                    />
                                )}
                                {integration.label}
                            </div>
                        );
                        const sections = integration.groups.map(group => (
                            <IntegrationUserDataSection
                                key={group.label}
                                group={group}
                                shouldDisplayGroupName={integration.groups.length > 1}
                            />
                        ));

                        return (
                            <div className={`rounded ${classNamesList.join(" ")}`} key={integration.id}>
                                {theme.isV3 ? (
                                    <ExpandingSection
                                        title={titleBlock}
                                        className="p-0"
                                        cardClassName={Styles["user-group-container"]}
                                        defaultExpanded={theme.isV3 ? integration.isDefault === true : true}
                                        onExpanded={metricPathPrefix && integration.type === IntegrationType.Service
                                            ? () => {
                                                const id = integration.id?.toString()
                                                    .match(/(urn:fq:workflow-service:[^,]+)/)?.[1];
                                                LoggingHandler.instance.metric([
                                                    ...metricPathPrefix, id, "expanded",
                                                ]);
                                            }
                                            : undefined}
                                    >

                                        {sections}
                                    </ExpandingSection>
                                ) : (
                                    <>
                                        {titleBlock}
                                        {sections}
                                    </>
                                )}

                            </div>
                        );
                    })}

                </div>

            </div>
        </div>
    );
}

IntegrationUserData.defaultProps = {
    metricPathPrefix: undefined,
};
